// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapActions, mapState } from 'vuex';
import config from '../../../share/config';
import alert from '../../../share/alert';
import handler from '../../../share/util/apiResultValidator';
import loading from '../../../share/components/vue/ProcessingIcon';
import util from '../../../share/util/utils';

export default {
  data() {
    return {
      processing: false,
      attentionIgOn: false,
      vehicleType: null
    };
  },
  components: {
    'processing-view': loading
  },

  computed: {
    ...mapState({
      // 車種情報取得の接続状況
      vehicleStatus: state => state.vehicle.status,
      vehicleError: state => state.vehicle.error,
      vehicleWatcher: state => state.vehicle.watcher,
      progress: state => state.vehicle.progress,
      vehicleInfo: state => state.vehicle.vehicleInfo,
      // 車両のキャッシュ情報
      oldVehicleInfo: state => state.cache.vehicleInfo,
      pairingAppId: state => state.netAppConnector.pairingAppId,
      isTestMode: state => state.netAppConnector.isTestMode,
      isTechnicalHq: state => state.userProperty.userInfo.isTechnicalHq,
      cachedTree: state => state.cache.systemTree
    }),
  },

  methods: {
    ...mapActions('app', ['setDirectMove']),
    ...mapActions('auth', ['logout']),
    ...mapActions('vehicle', ['updateVehicleInfo']),
    ...mapActions('cache', ['cacheSystemTree']),
    ...mapActions('observation', ['usageCountUp']),

    // リプロ実行前のチェック処理を開始 
    execute() {
      // 車両情報の取得ステータスを監視
      const watcherInstance = this.$watch('vehicleStatus', (val) => {
        // VIN 情報取得失敗の場合 
        if (val === config.POLLING_FAILURE) {
          watcherInstance();
          this.processing = false;
          // エラーメッセージの表示
          this.showFailedMessage('message.error_vin_confirm_title', this.vehicleError);
        }
        // VIN 情報取得完了
        else if (val === config.POLLING_COMPLETED) {
          watcherInstance();
          this.processing = false;
          // VIN情報・車台番号が取得できない場合 
          if (this.vehicleInfo.vehicle.vin.type === config.VEHICLEINFO_TYPE_UN_KNOWN) {
            this.updateBattery();
            return;
          }
          // 取得した管理番号の種別が一致している場合
          if (this.vehicleInfo.vehicle.is_vin_changed === false) {
            this.updateBattery();
            return;
          }
          const messages = this.$t('message.message_change_vehicle_info_error');
          alert.showWarning(this, null, messages, () => {
            // 車両情報が異なるためエラーとして start 画面に戻す 
            this.$router.push({ name: 'home', params: { isAuto: true, isConfirmSkip: true } });
            return;
          });
        }
      });

      // リプロが開始された場合にはツリーで選択された選択状態を解除しておく
      this.cacheSystemTree(util.relaseTreeSelected(this.cachedTree[config.EXECUE_FUNCTION_TYPE_RPR]));

      if (this.isTestMode) {
        // リプロ実行画面に遷移
        this.$router.push({ name: 'execution' });
      } else {
        // VIN情報の再取得開始 
        this.processing = true;
        this.vehicleType = "vin";
        const data = {
          netAppId: this.pairingAppId,
          endPoint: '/vehicle/vin',
          this: this,
          type: config.REPRO
        };
        this.updateVehicleInfo(data);
      }
    },

    // システム情報画面に戻る
    cancel() {
      // リプロ実行中の状態をキャンセル 
      this.$router.push({ name: 'systemInformation', params: { previousDisplayName: 'attention' } });
    },

    // 電圧取得の開始 
    updateBattery() {
      // 車両情報の取得ステータスを監視
      const watcherInstance = this.$watch('vehicleStatus', (val) => {
        // 画面遷移時の電圧取得失敗はエラーとしない。 
        if (val === config.POLLING_FAILURE || val === config.POLLING_COMPLETED) {
          watcherInstance();
          //reproのカウントだけを1つ加算する
          this.usageCountUp({ type: config.USAGE_COUNT_REPRO });
          this.processing = false;
          this.setDirectMove({}); // 移動情報をクリア
          // リプロ実行画面に遷移
          this.$router.push({ name: 'execution' });
        }
      });
      // 電圧情報の再取得開始 
      this.processing = true;
      this.vehicleType = "battery";
      const data = {
        netAppId: this.pairingAppId,
        endPoint: '/vehicle/battery',
        this: this,
        type: config.REPRO
      };
      this.updateVehicleInfo(data);
    },

    // エラーメッセージ表示処理 
    showFailedMessage(errTitleId, errorInfo) {
      const error = errorInfo;
      let messages = null;
      // メンテナンスエラーの場合はエラー表示を変更する 
      if (!error.validatorResult || error.validatorResult.causeType === handler.causeTypes.maintenanceError) {
        messages = error.message;
      }
      else {
        const errTitle = this.$t(errTitleId);
        messages = {
          mainMessage: errTitle,
          messageList: []
        };
        const messageFirst = {
          label: this.$t('message.common_label_error_message'),
          message: error.message
        };
        messages.messageList.push(messageFirst);
      }

      alert.showError(this, null, messages, () => {
        if (error.behaviorType === handler.behaviorTypes.logout) {
          this.logout(this);
        }
      });
    }
  },

  beforeUnmount() {
    this.$stopInterval(this.vehicleWatcher);
  },
};